import LoginResponse from '@/types/LoginResponse';
import router from '@/router';
import { sendLoginRequest, getNewAccessToken } from './api';

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const EXPIRES_IN_KEY = 'expires_in';
const USERNAME_KEY = 'username';

export const getAuthorizationHeaders = (): object => {
  return { Authorization: 'Basic d2ViLWFwcDo=' };
};

export const requireAuth = (to: any, from: any, next: any): void => {
  if (!isLoggedIn()) {
    next({
      path: '/login',
      query: { redirect: to.fullPath },
    });
  } else {
    next();
  }
};

const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

const getUsername = (): string | null => {
  return localStorage.getItem(USERNAME_KEY);
};

export const isLoggedIn = (): boolean => {
  return getRefreshToken() !== null && getRefreshToken() !== undefined;
};

const getTokenExpirationDate = (): Date | null  => {
  const expirationDateString: string | null = localStorage.getItem(EXPIRES_IN_KEY);
  if (expirationDateString !== null) {
    const expirationDate: Date = new Date(expirationDateString);

    return expirationDate;
  }

  return null;
};

const isTokenExpired = (): boolean => {
  const accessToken: string | null = localStorage.getItem(ACCESS_TOKEN_KEY);
  const expirationDate: Date | null = getTokenExpirationDate();

  return accessToken === null || expirationDate === null || expirationDate < new Date();
};

export const getAccessToken = async (): Promise<string | null> => {
  if (!isLoggedIn()) { return null; }

  if (isTokenExpired()) {
    await updateAccessToken();
  }

  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

const clearRefreshToken = (): void => {
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

const clearAccessToken = (): void => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
};

const clearExpirationDate = (): void => {
  localStorage.removeItem(EXPIRES_IN_KEY);
};

const clearUserName = (): void => {
  localStorage.removeItem(USERNAME_KEY);
};

export const convertExpirationSecondsToDate = (expirationInSeconds: number): Date => {
  const expiresIn: Date = new Date();

  expiresIn.setSeconds(expiresIn.getSeconds() + expirationInSeconds);

  return expiresIn;
};

const addTokensToStorage = (loginResponse: LoginResponse): void => {
  localStorage.setItem(ACCESS_TOKEN_KEY, loginResponse.access_token);
  localStorage.setItem(REFRESH_TOKEN_KEY, loginResponse.refresh_token);
  localStorage.setItem(EXPIRES_IN_KEY, loginResponse.expires_in.toString());
};

const addUsernameToStorage = (username: string): void => {
  localStorage.setItem(USERNAME_KEY, username);
};

const loginFormData = (username: string, password: string): FormData => {
  const formData: FormData = new FormData();

  formData.append('username', username);
  formData.append('password', password);
  formData.append('grant_type', 'password');

  return formData;
};

const refreshTokenFormData = (): FormData => {
  const formData: FormData = new FormData();
  const refreshToken: string | null = getRefreshToken();
  const username: string | null = getUsername();

  if (refreshToken !== null && username !== null) {
    formData.append(USERNAME_KEY, username);
    formData.append(REFRESH_TOKEN_KEY, refreshToken);
    formData.append('grant_type', 'refresh_token');
  }

  return formData;
};

const updateAccessToken = async (): Promise<void> => {
  try {
    const response: LoginResponse = await getNewAccessToken(refreshTokenFormData());
    addTokensToStorage(response);
  } catch {
    logout();
  }
};

export const login = async (username: string, password: string): Promise<void> => {
  const formData: FormData = loginFormData(username, password);
  const response: LoginResponse = await sendLoginRequest(formData);
  addTokensToStorage(response);
  addUsernameToStorage(username);
  router.push('/');
};

export const logout = (): void => {
  clearRefreshToken();
  clearAccessToken();
  clearExpirationDate();
  clearUserName();
  router.push('login');
};

